import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError, orders as ordersApi, documents as docApi } from "../api/client";
import type { OrderDetail, OrderEvent, OrderState } from "../api/types";
import { TERMINAL_STATES } from "../api/types";
import { useMe } from "../auth/session";
import { QrImage } from "../components/QrImage";
import { QuoteRows } from "../components/QuoteBreakdown";
import { useSandbox } from "../components/SandboxPanel";
import { Page } from "../components/Shell";
import { ErrorBox, Icon, Modal, Spinner, StatePill, YSymbol, copyText, useToast, usePrefersReducedMotion } from "../components/ui";
import { useAsync, usePolling } from "../hooks/useAsync";
import { useCountdown } from "../hooks/useCountdown";
import { fmt, stateLabel, stateTone, t } from "../i18n";
import { dateTimeShort, fileSize, shortHash, timeHM, timeHMS } from "../lib/format";
import { brl, costBp, effectiveRateE8, mmss, pct, rate, usd } from "../lib/money";
import { railAsset, railLabel, railNetworkName } from "../lib/rail";

export function Pedido() {
  const { id = "" } = useParams();
  const { data, error, loading, reload, setData } = useAsync((signal) => ordersApi.get(id, signal), [id]);
  const sb = useSandbox();
  const state = data?.order.state;
  const terminal = state ? TERMINAL_STATES.has(state) : false;

  const refresh = useCallback(async () => {
    try {
      const d = await ordersApi.get(id);
      setData(d);
    } catch {
      /* mantém o último estado; o próximo tick tenta de novo */
    }
  }, [id, setData]);

  usePolling(refresh, 2000, Boolean(data) && !terminal);

  useEffect(() => {
    if (data) sb.setOrder({ id: data.order.id, state: data.order.state, totalCents: data.order.totalCents });
    sb.setOnSimulated(() => void refresh());
    return () => {
      sb.setOrder(null);
      sb.setOnSimulated(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.order.id, data?.order.state, data?.order.totalCents, refresh]);

  if (loading && !data) return <Page title={t.order.title}><Spinner /></Page>;
  if (error || !data) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <Page title={t.order.title} backTo="/">
        <ErrorBox error={notFound ? new Error(t.order.notFound) : error} onRetry={reload} />
      </Page>
    );
  }
  if (data.order.state === "awaiting_funds" && data.pixCharge) return <PixView data={data} onChanged={refresh} />;
  return <OrderView data={data} />;
}

/* ---------------- Pix ---------------- */
function PixView({ data, onChanged }: { data: OrderDetail; onChanged: () => Promise<void> }) {
  const me = useMe();
  const toast = useToast();
  const navigate = useNavigate();
  const pix = data.pixCharge!;
  const [cancelAsk, setCancelAsk] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const countdown = useCountdown(pix.expiresAt, () => void onChanged());
  const supplier = data.beneficiary?.legalName ?? "—";
  const network = railNetworkName(data.beneficiary?.rail);
  const invoiceDoc = data.documents.find((d) => d.kind === "invoice");

  async function copy() {
    const ok = await copyText(pix.qrCodePayload);
    toast(ok ? t.common.copied : t.common.copyFailed);
  }

  async function cancel() {
    setCancelling(true);
    try {
      await ordersApi.cancel(data.order.id);
      toast(t.pix.cancelled);
      navigate("/");
    } catch (e) {
      toast(e instanceof Error ? e.message : t.common.errorGeneric);
    } finally {
      setCancelling(false);
      setCancelAsk(false);
    }
  }

  const stepOrder: OrderState[] = ["quoted", "awaiting_funds", "funds_received", "executing", "settled"];
  const quotedAt = data.events.find((e) => e.type === "quote_locked")?.at ?? data.order.createdAt;

  return (
    <Page
      title={t.pix.title}
      crumb={fmt(t.order.crumb, { id: data.order.id })}
      backTo="/"
      actions={
        <button type="button" className="btn ghost" onClick={() => setCancelAsk(true)}>
          {t.pix.cancelOrder}
        </button>
      }
    >
      <div className="two n380">
        <div className="card pixgrid" style={{ padding: 28, display: "grid", gridTemplateColumns: "auto minmax(0,1fr)", gap: 28, alignItems: "start" }}>
          <QrImage value={pix.qrCodePayload} alt={t.pix.qrAlt} />
          <div className="stack" style={{ gap: 14, width: "100%" }}>
            <div className="stack" style={{ gap: 4 }}>
              <span className="lbl">{t.pix.payExactly}</span>
              <span className="bigval xl" data-testid="pix-amount">{brl(pix.amountCents)}</span>
            </div>
            {pix.status === "funded" ? (
              <div className="lock" style={{ margin: 0 }} role="status" aria-live="polite">
                {Icon.check()}
                {t.pix.fundedBanner}
              </div>
            ) : (
              <div className={`lock ${countdown.low ? "low" : ""}`} style={{ margin: 0 }} role="timer">
                {Icon.lock()}
                {countdown.expired ? t.pix.expiredBanner : t.pix.validFor}
                {!countdown.expired && <span className="t mono">{mmss(countdown.secondsLeft)}</span>}
              </div>
            )}
            <div className="field">
              <span className="lbl">{t.pix.copyPaste}</span>
              <div className="pixcode">
                <span className="mono code" title={pix.qrCodePayload}>{pix.qrCodePayload}</span>
                <button type="button" className="btn ghost xs" onClick={copy}>
                  {Icon.copy()}
                  {t.common.copy}
                </button>
              </div>
            </div>
            <div className="ink2" style={{ fontSize: 13, lineHeight: 1.5, display: "flex", flexDirection: "column", gap: 6 }}>
              <span dangerouslySetInnerHTML={{ __html: fmt(t.pix.payee, { payee: `<b>${t.brand.partnerIpCnpj}</b>` }) }} />
              <span>{t.pix.divergent}</span>
            </div>
            <div className="row" style={{ gap: 8, fontSize: 13 }}>
              <span className="spin" aria-hidden="true" />
              <span className="muted">{t.pix.waiting}</span>
            </div>
          </div>
        </div>
        <div className="stack">
          <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
            <h2 style={{ fontSize: 15 }}>{fmt(t.pix.order, { id: data.order.id })}</h2>
            <div className="steps">
              {stepOrder.map((s, i) => {
                const idx = 1; // awaiting_funds
                const cls = i < idx ? "done" : i === idx ? "now" : "";
                return (
                  <div key={s} className={`step ${cls}`}>
                    <i aria-hidden="true" />
                    {stateLabel(s)}
                    {s === "quoted" ? ` · ${timeHM(quotedAt)}` : ""}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
            <span className="lbl">{t.pix.summary}</span>
            <div className="kv"><span className="k">{t.pix.supplier}</span><span className="v" style={{ fontWeight: 600 }}>{supplier}</span></div>
            <div className="kv"><span className="k">{t.pix.sending}</span><span className="v mono">{railAsset(data.beneficiary?.rail)} {usd(data.order.usdCents).replace("USD ", "")} · {network}</span></div>
            {data.beneficiary?.rail.kind === "stablecoin" && <div className="kv"><span className="k">{t.pix.parity}</span><span className="v mono">1 {data.beneficiary.rail.asset} = 1 USD</span></div>}
            <div className="kv"><span className="k">{t.pix.lockedRate}</span><span className="v mono">{rate(data.quote.rateE8)}</span></div>
            {invoiceDoc && <div className="kv"><span className="k">{t.pix.invoice}</span><span className="v mono">{invoiceDoc.fileName}</span></div>}
          </div>
          <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
            <span className="lbl">{t.pix.lockedCost}</span>
            <QuoteRows quote={data.quote} network={network} />
            <div className="qrow sum">
              <span className="k">{t.pix.totalPix}</span>
              <span className="v mono">{brl(data.quote.totalCents)}</span>
            </div>
          </div>
        </div>
      </div>
      <p className="legal">{me.legalTexts?.partnerDisclosure ?? t.brand.legalFooter}</p>
      {cancelAsk && (
        <Modal title={t.pix.cancelOrder} onClose={() => setCancelAsk(false)}>
          <p>{t.pix.cancelConfirm}</p>
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="btn ghost" onClick={() => setCancelAsk(false)}>{t.common.back}</button>
            <button type="button" className="btn crit" onClick={cancel} disabled={cancelling}>{t.pix.cancelOrder}</button>
          </div>
        </Modal>
      )}
    </Page>
  );
}

/* ---------------- Pedido ---------------- */
function OrderView({ data }: { data: OrderDetail }) {
  const me = useMe();
  const toast = useToast();
  const o = data.order;
  const q = data.quote;
  const reduced = usePrefersReducedMotion();
  const prevState = useRef<OrderState | null>(null);
  const [wag, setWag] = useState(false);
  const [nps, setNps] = useState<number | null>(o.nps ?? null);
  const [npsBusy, setNpsBusy] = useState(false);

  // a cauda abana uma vez quando o pagamento chega
  useEffect(() => {
    if (prevState.current && prevState.current !== "settled" && o.state === "settled" && !reduced) {
      setWag(true);
      const id = window.setTimeout(() => setWag(false), 800);
      return () => window.clearTimeout(id);
    }
    prevState.current = o.state;
    return undefined;
  }, [o.state, reduced]);
  useEffect(() => {
    prevState.current = o.state;
  }, [o.state]);

  const supplier = data.beneficiary?.legalName ?? "—";
  const settledAt = o.settledAt ?? data.events.filter((e) => e.toState === "settled").at(-1)?.at;
  const eff = effectiveRateE8(o.totalCents, o.usdCents);
  const fees = BigInt(o.totalCents) - BigInt(q.baseCents);
  const cost = costBp(o.totalCents, q.baseCents);
  const invoiceDoc = data.documents.find((d) => d.kind === "invoice");
  const receiptDoc = data.documents.find((d) => d.kind === "receipt");
  const fxDoc = data.documents.find((d) => d.kind === "fx_contract");
  const hash = data.execution?.txHash;
  const repeatQuery = `?fornecedor=${encodeURIComponent(data.beneficiary?.id ?? "")}&usd=${o.usdCents}&natureza=${q.nature ?? "goods_import"}`;

  async function sendNps(n: number) {
    setNpsBusy(true);
    try {
      await ordersApi.nps(o.id, n);
      setNps(n);
      toast(fmt(t.order.npsThanks, { n }));
    } catch (e) {
      toast(e instanceof Error ? e.message : t.common.errorGeneric);
    } finally {
      setNpsBusy(false);
    }
  }

  const banner = (() => {
    if (o.state === "under_review") return { cls: "warn", text: t.order.bannerReview };
    if (o.state === "failed") return { cls: "crit", text: t.order.bannerFailed };
    if (o.state === "expired") return { cls: "", text: t.order.bannerExpired };
    if (o.state === "refunded") return { cls: "", text: t.order.bannerRefunded };
    if (["funds_received", "executing", "sent", "quoted"].includes(o.state)) return { cls: "", text: t.order.bannerFollowing, spin: true };
    return null;
  })();

  return (
    <Page
      title={t.order.title}
      crumb={fmt(t.order.crumb, { id: o.id })}
      backTo="/"
      actions={
        o.state === "settled" ? (
          <Link to={`/pagamentos/novo${repeatQuery}`} className="btn dark">
            {Icon.arrow()}
            {t.order.repeat}
          </Link>
        ) : o.state === "expired" || o.state === "refunded" || o.state === "failed" ? (
          <Link to={`/pagamentos/novo${repeatQuery}`} className="btn">
            {t.order.newQuote}
          </Link>
        ) : (
          <Link to="/" className="btn ghost">
            {t.order.backToPanel}
          </Link>
        )
      }
    >
      <div className="card" style={{ padding: "22px 24px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <div className="stack" style={{ gap: 4 }}>
          <span className="muted small">{fmt(t.order.sub, { id: o.id, supplier })}</span>
          <div className="row" style={{ alignItems: "baseline", gap: 14 }}>
            <span className="bigval">{usd(o.usdCents)}</span>
            <span className="mono ink2">{["settled", "sent", "executing", "funds_received", "failed", "refunded"].includes(o.state) ? fmt(t.order.paidWith, { brl: brl(o.totalCents) }) : fmt(t.order.quotedAt, { brl: brl(o.totalCents) })}</span>
          </div>
        </div>
        <div className="row" style={{ marginLeft: "auto" }}>
          {o.state === "settled" && <YSymbol size={26} wag={wag} style={{ color: "var(--musgo)" }} />}
          <StatePill state={o.state} suffix={o.state === "settled" && settledAt ? dateTimeShort(settledAt) : undefined} />
        </div>
      </div>

      {banner && (
        <div className={`card banner ${banner.cls}`} role="status">
          {banner.spin && <span className="spin" aria-hidden="true" />}
          <span>{banner.text}</span>
        </div>
      )}
      {o.reviewReasons && o.reviewReasons.length > 0 && o.state === "under_review" && (
        <div className="card" style={{ padding: "12px 18px", fontSize: 13 }}>
          <span className="lbl">{t.order.reviewReasons}</span>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {o.reviewReasons.map((r) => (
              <li key={r}>{t.reviewReasons[r] ?? r}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="two narrow">
        <div className="card" style={{ padding: "22px 24px" }}>
          <h2 style={{ fontSize: 15, marginBottom: 18 }}>{t.order.timeline}</h2>
          <Timeline events={data.events} data={data} />
        </div>
        <div className="stack">
          <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10 }}>
            <h2 style={{ fontSize: 15 }}>{t.order.documents}</h2>
            {o.state === "settled" && (
              <DocRow title={t.order.docReceipt} sub={t.order.docReceiptSub} href={ordersApi.documentUrl(o.id, "receipt")} />
            )}
            {(fxDoc || data.execution?.fxContractRef) && (
              <DocRow title={t.order.docFx} sub={fxDoc ? t.order.docFxSub : `${t.order.docFxSub} · ${data.execution?.fxContractRef ?? ""}`} href={fxDoc ? docApi.url(fxDoc.id) : ordersApi.documentUrl(o.id, "fx_contract")} />
            )}
            {o.state === "settled" && !fxDoc && !data.execution?.fxContractRef && <DocRow title={t.order.docFx} sub={t.order.docFxPending} />}
            <DocRow title={fmt(t.order.docInvoice, { name: invoiceDoc?.fileName ?? "" })} sub={`${invoiceDoc?.mimeType?.startsWith("image/") ? "Imagem" : "PDF"}${invoiceDoc?.sizeBytes ? ` · ${fileSize(invoiceDoc.sizeBytes)}` : ""} · ${t.order.docInvoiceSub}`} href={invoiceDoc ? docApi.url(invoiceDoc.id) : ordersApi.documentUrl(o.id, "invoice")} />
            {receiptDoc && o.state !== "settled" && <DocRow title={t.order.docReceipt} sub={t.order.docReceiptSub} href={docApi.url(receiptDoc.id)} />}
          </div>
          <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
            <span className="lbl">{t.order.details}</span>
            <div className="kv"><span className="k">{t.order.lockedRate}</span><span className="v mono">1 USD = {rate(q.rateE8)}</span></div>
            <div className="kv"><span className="k">{t.order.effectiveRate}</span><span className="v mono">1 USD = {rate(eff)}</span></div>
            <div className="kv"><span className="k">{t.order.totalCost}</span><span className="v mono">{pct(cost)} · {brl(fees)}</span></div>
            <div className="kv"><span className="k">{t.order.nature}</span><span className="v">{t.newPayment.natures[q.nature ?? ""] ?? q.nature ?? "—"}</span></div>
            {o.reference && <div className="kv"><span className="k">{t.order.reference}</span><span className="v">{o.reference}</span></div>}
            {data.beneficiary && <div className="kv"><span className="k">{t.pix.sending}</span><span className="v mono">{railLabel(data.beneficiary.rail)}</span></div>}
            {hash && <div className="kv"><span className="k">{t.order.hash}</span><span className="v mono wrap">{hash}</span></div>}
            {data.execution?.bankRef && <div className="kv"><span className="k">{t.order.bankRef}</span><span className="v mono wrap">{data.execution.bankRef}</span></div>}
          </div>
          {o.state === "settled" && (
            <div className="card" style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{t.order.npsTitle}</span>
              <div className="nps" role="group" aria-label={t.order.npsTitle}>
                {Array.from({ length: 11 }, (_, n) => (
                  <button type="button" key={n} aria-pressed={nps === n} onClick={() => sendNps(n)} disabled={npsBusy}>
                    {n}
                  </button>
                ))}
              </div>
              <div className="row between muted" style={{ fontSize: 11 }}>
                <span>{t.order.npsLow}</span>
                <span>{t.order.npsHigh}</span>
              </div>
              {nps !== null && <span className="muted small">{fmt(t.order.npsThanks, { n: nps })}</span>}
            </div>
          )}
        </div>
      </div>
      <p className="legal">{me.legalTexts?.partnerDisclosure ?? t.brand.legalFooter}</p>
    </Page>
  );
}

function DocRow({ title, sub, href }: { title: string; sub: string; href?: string }) {
  return (
    <div className="doc">
      {Icon.doc()}
      <div className="txt">
        <b>{title}</b>
        <small>{sub}</small>
      </div>
      {href && (
        <a className="linkbtn act" href={href} target="_blank" rel="noopener">
          {Icon.download()}
          {t.common.download}
        </a>
      )}
    </div>
  );
}

function payloadStr(p: Record<string, unknown> | null | undefined, k: string): string | undefined {
  const v = p?.[k];
  return typeof v === "string" || typeof v === "number" ? String(v) : undefined;
}

export function describeEvent(e: OrderEvent, data: Pick<OrderDetail, "order" | "quote" | "execution">): { title: string; desc: string; who: string; tone: string } {
  const p = e.payload ?? null;
  const total = brl(data.order.totalCents);
  const paid = payloadStr(p, "paidCents");
  const vars = {
    rate: rate(data.quote.rateE8),
    total,
    paid: paid ? brl(paid) : total,
    hash: shortHash(payloadStr(p, "txHash") ?? data.execution?.txHash),
    code: payloadStr(p, "failureCode") ?? data.execution?.failureCode ?? "erro do parceiro",
  };
  const title = t.events[e.type] ?? stateLabel(e.toState);
  const desc = fmt(t.eventDesc[e.type] ?? t.stateMessages[e.toState] ?? "", vars);
  const actor = t.actors[e.actorKind] ?? e.actorKind;
  const extra = payloadStr(p, "endToEndId") ?? payloadStr(p, "txid") ?? payloadStr(p, "executionId");
  const who = extra ? `${actor} · ${shortHash(extra, 6, 4)}` : actor;
  return { title, desc, who, tone: stateTone(e.toState) };
}

function Timeline({ events, data }: { events: OrderEvent[]; data: OrderDetail }) {
  const list = [...events].sort((a, b) => b.seq - a.seq);
  if (!list.length) return <div className="muted small">{t.common.empty}</div>;
  return (
    <div className="tl">
      {list.map((e) => {
        const d = describeEvent(e, data);
        return (
          <div key={e.seq} className={`ev ${d.tone}`}>
            <span className="t mono">{timeHMS(e.at)}</span>
            <span className="d"><i aria-hidden="true" /></span>
            <div className="b">
              <b>{d.title}</b>
              {d.desc && <span>{d.desc}</span>}
              <small>{d.who}</small>
            </div>
          </div>
        );
      })}
    </div>
  );
}
